// Location data for the /locations SEO architecture.
// Content is grounded in real, publicly verifiable facts (TSIIC/APIIC industrial
// parks, known regional industries, approximate road distance from our
// Peerzadiguda, Hyderabad facility) rather than templated city-name swaps.
// Vasavi does not have branch offices in these cities — pages are framed
// honestly as design-in-Hyderabad / deliver-and-erect-on-site.

export const states = [
  {
    slug: "telangana",
    name: "Telangana",
    intro:
      "Telangana is home to Vasavi Structures' base of operations in Peerzadiguda, Hyderabad. We design and fabricate PEB structures, industrial sheds and steel buildings at our Hyderabad facility and erect them on-site across the state's industrial belt — from the pharma and engineering clusters around Patancheru and Sangareddy to the textile and agro-processing hubs further out.",
    heroNote: "Home state — Hyderabad facility, statewide erection.",
  },
  {
    slug: "andhra-pradesh",
    name: "Andhra Pradesh",
    intro:
      "Andhra Pradesh runs three recognised industrial corridors (Visakhapatnam–Chennai, Chennai–Bengaluru and the Hyderabad–Bengaluru corridor) with APIIC-developed industrial parks across Vijayawada, Visakhapatnam, Guntur, Kakinada, Tirupati and Nellore. Vasavi Structures designs and fabricates PEB buildings and industrial sheds at our Hyderabad facility and transports and erects them at AP project sites.",
    heroNote: "Statewide delivery from our Hyderabad manufacturing base.",
  },
];

export const locations = [
  // ---------------- TELANGANA ----------------
  {
    slug: "hyderabad",
    stateSlug: "telangana",
    name: "Hyderabad",
    isPrimary: true,
    distance: "Our facility is based here",
    intro:
      "Hyderabad is where Vasavi Structures is headquartered — our design, fabrication and project management team operates out of Peerzadiguda. Most of our completed PEB, industrial shed and steel fabrication work has been delivered within the Hyderabad metropolitan region, including areas close to the ORR industrial belt.",
    industrialZones: [
      "TSIIC Industrial Estate, Patancheru (Sangareddy district)",
      "Bollaram–Jeedimetla industrial belt",
      "Uppal & ECIL industrial pockets (near our facility)",
      "Shamshabad TSIIC zone",
    ],
    sectors: [
      { name: "Pharmaceuticals & bulk drugs", note: "Patancheru–Bollaram is one of India's established pharma manufacturing belts." },
      { name: "General & precision engineering", note: "Dense cluster of ancillary and component manufacturing units across the ORR industrial nodes." },
      { name: "Logistics & warehousing", note: "Growing distribution-centre demand along the Outer Ring Road corridor." },
      { name: "IT-adjacent light manufacturing & auto ancillary", note: "Concentrated around Shamshabad and the western industrial corridor." },
    ],
    faqs: [
      {
        q: "Does Vasavi Structures have an office in Hyderabad?",
        a: "Yes — our design and fabrication facility is based in Peerzadiguda, Hyderabad. This is our only physical location; all other cities are served through project-based delivery and erection.",
      },
      {
        q: "What areas of Hyderabad do you cover?",
        a: "We take on projects across the Hyderabad metropolitan region, including the ORR industrial belt, Patancheru, Bollaram, Shamshabad and surrounding industrial pockets. Site visits are easiest to schedule within this radius.",
      },
      {
        q: "How fast can a PEB shed be erected in Hyderabad?",
        a: "Erection timelines depend on structure size and site readiness. As a general industry benchmark, PEB structures typically erect 50–65% faster than equivalent RCC construction — we'll give you a project-specific timeline after a site assessment.",
      },
    ],
  },
  {
    slug: "warangal",
    stateSlug: "telangana",
    name: "Warangal",
    distance: "~150 km from our Peerzadiguda facility",
    intro:
      "Warangal is Telangana's textile and agro-processing hub, anchored by the Kakatiya Mega Textile Park. Vasavi Structures designs and fabricates PEB buildings and industrial sheds at our Hyderabad facility and delivers, transports and erects them at Warangal project sites.",
    industrialZones: [
      "Kakatiya Mega Textile Park, Shayampet",
      "Warangal TSIIC industrial zones",
    ],
    sectors: [
      { name: "Textiles & garmenting", note: "The Kakatiya Textile Park is a dedicated cluster for spinning, weaving and garment units requiring large-span industrial sheds." },
      { name: "Agro-processing", note: "Regional grain and cotton trade drives demand for storage and warehouse structures." },
    ],
    faqs: [
      {
        q: "Do you deliver PEB structures to Warangal?",
        a: "Yes. We design and fabricate at our Hyderabad facility and handle transport and on-site erection for Warangal projects, including textile-park and agro-processing sheds.",
      },
      {
        q: "Can you design a shed for a textile unit near Kakatiya Textile Park?",
        a: "Yes — textile and garmenting sheds typically need wide clear spans and specific ventilation; we design to your production-line layout and load requirements.",
      },
    ],
  },
  {
    slug: "sangareddy",
    stateSlug: "telangana",
    name: "Sangareddy",
    distance: "~50 km from our Peerzadiguda facility",
    intro:
      "Sangareddy district includes the Patancheru TSIIC Industrial Estate and the Bollaram belt — one of Telangana's oldest and densest industrial zones, close enough to our Hyderabad facility for frequent site visits and fast turnaround.",
    industrialZones: [
      "TSIIC Industrial Estate, Patancheru",
      "Bollaram industrial area",
    ],
    sectors: [
      { name: "Pharmaceuticals & chemicals", note: "One of India's established pharma-manufacturing belts, with associated demand for cleanroom-adjacent and utility sheds." },
      { name: "General manufacturing", note: "Long-established engineering and ancillary units across Patancheru." },
    ],
    faqs: [
      {
        q: "Is Sangareddy within your regular service area?",
        a: "Yes — Patancheru and Bollaram are close to our Hyderabad facility, so this is one of the areas we can visit and quote on quickly.",
      },
    ],
  },
  {
    slug: "nalgonda",
    stateSlug: "telangana",
    name: "Nalgonda",
    distance: "~90 km from our Peerzadiguda facility",
    intro:
      "Nalgonda has a dedicated TSIIC industrial park serving the district's manufacturing and processing units. Vasavi Structures fabricates in Hyderabad and delivers PEB and industrial shed structures to Nalgonda project sites.",
    industrialZones: ["TSIIC Industrial Park, Nalgonda"],
    sectors: [
      { name: "General manufacturing & processing", note: "District-level industrial units served by the TSIIC estate." },
      { name: "Agriculture-linked storage", note: "Godowns and storage sheds for regional agri-trade." },
    ],
    faqs: [
      {
        q: "What kind of structures do you build in Nalgonda?",
        a: "Mostly industrial sheds, storage godowns and PEB buildings for units operating out of or near the TSIIC Nalgonda industrial park.",
      },
    ],
  },
  {
    slug: "karimnagar",
    stateSlug: "telangana",
    name: "Karimnagar",
    distance: "~165 km from our Peerzadiguda facility",
    intro:
      "Karimnagar is a regional trade and agro-processing centre in north Telangana. We design PEB structures at our Hyderabad facility and manage transport and erection for Karimnagar projects.",
    industrialZones: ["Karimnagar district industrial estates"],
    sectors: [
      { name: "Agro-processing & rice milling", note: "Karimnagar sits in a major paddy-growing belt, driving demand for milling and storage structures." },
      { name: "Granite & allied industries", note: "The region has an established granite processing base requiring heavy-duty sheds." },
    ],
    faqs: [
      {
        q: "Do you build sheds for rice mills or granite units in Karimnagar?",
        a: "Yes — both require heavy-duty industrial sheds with specific floor-load and ventilation requirements, which we design around your equipment layout.",
      },
    ],
  },
  {
    slug: "nizamabad",
    stateSlug: "telangana",
    name: "Nizamabad",
    distance: "~175 km from our Peerzadiguda facility",
    intro:
      "Nizamabad is known for its turmeric trade and agro-processing base in north Telangana. Vasavi Structures fabricates PEB and industrial shed structures in Hyderabad and delivers to Nizamabad project sites.",
    industrialZones: ["Nizamabad district industrial areas"],
    sectors: [
      { name: "Turmeric & agro-commodity trade", note: "Home to one of Asia's major turmeric markets, driving demand for storage and warehouse structures." },
      { name: "Agro-processing", note: "Regional grain and oilseed processing units." },
    ],
    faqs: [
      {
        q: "Can you build storage godowns near the Nizamabad turmeric market?",
        a: "Yes — we design storage and warehouse sheds sized to your commodity volumes and turnover requirements.",
      },
    ],
  },
  {
    slug: "khammam",
    stateSlug: "telangana",
    name: "Khammam",
    distance: "~200 km from our Peerzadiguda facility",
    intro:
      "Khammam sits near Telangana's coal and mineral belt and serves as a regional agro-trade centre. We design PEB structures at our Hyderabad facility and manage delivery and erection for Khammam projects.",
    industrialZones: ["Khammam district industrial areas"],
    sectors: [
      { name: "Mining-linked & allied industries", note: "Proximity to the Singareni coalfields region supports ancillary and equipment-storage demand." },
      { name: "Agro-processing", note: "Cotton and paddy trade in the surrounding districts." },
    ],
    faqs: [
      {
        q: "Do you serve Khammam for industrial shed projects?",
        a: "Yes, we take on projects in Khammam — design and fabrication happen at our Hyderabad facility, with transport and erection handled on-site.",
      },
    ],
  },

  // ---------------- ANDHRA PRADESH ----------------
  {
    slug: "vijayawada",
    stateSlug: "andhra-pradesh",
    name: "Vijayawada",
    distance: "~275 km from our Peerzadiguda facility",
    intro:
      "Vijayawada is Andhra Pradesh's central logistics and trade hub, sitting at the junction of major road and rail corridors. Vasavi Structures designs and fabricates PEB buildings at our Hyderabad facility and delivers, transports and erects them at Vijayawada project sites.",
    industrialZones: ["Gannavaram–Kankipadu Node (Visakhapatnam–Chennai Industrial Corridor)"],
    sectors: [
      { name: "Logistics & warehousing", note: "Central location makes Vijayawada a natural distribution-centre hub for the state." },
      { name: "Agro-trade & processing", note: "Major regional market for grain, cotton and allied commodities." },
    ],
    faqs: [
      {
        q: "Do you deliver PEB warehouses to Vijayawada?",
        a: "Yes — Vijayawada's role as a logistics hub means warehouse and distribution-centre PEB structures are a common project type here for us.",
      },
    ],
  },
  {
    slug: "visakhapatnam",
    stateSlug: "andhra-pradesh",
    name: "Visakhapatnam",
    distance: "~620 km from our Peerzadiguda facility",
    intro:
      "Visakhapatnam anchors the Visakhapatnam–Chennai Industrial Corridor and is a major port, steel and pharmaceutical manufacturing centre. Vasavi Structures fabricates at our Hyderabad facility and manages transport and erection for Visakhapatnam project sites.",
    industrialZones: ["Visakhapatnam Node, Visakhapatnam–Chennai Industrial Corridor", "APIIC industrial estates, Visakhapatnam"],
    sectors: [
      { name: "Port-linked logistics", note: "One of India's major ports, driving significant warehousing and cargo-handling infrastructure demand." },
      { name: "Steel & heavy industry", note: "Home to large-scale steel manufacturing and ancillary units." },
      { name: "Pharmaceuticals", note: "Growing pharma-manufacturing presence in the surrounding industrial estates." },
    ],
    faqs: [
      {
        q: "Given the distance, can you realistically deliver to Visakhapatnam?",
        a: "Yes — we regularly plan transport and erection logistics for sites beyond Telangana as part of the project scope; distance affects lead time and logistics cost, which we account for in the quote.",
      },
    ],
  },
  {
    slug: "guntur",
    stateSlug: "andhra-pradesh",
    name: "Guntur",
    distance: "~300 km from our Peerzadiguda facility",
    intro:
      "Guntur is one of India's largest chilli and cotton trading centres, generating substantial demand for storage and warehouse infrastructure. Vasavi Structures designs PEB and industrial shed structures in Hyderabad and delivers them to Guntur project sites.",
    industrialZones: ["Guntur district industrial estates"],
    sectors: [
      { name: "Agro-commodity trade", note: "Home to one of Asia's largest chilli markets, alongside major cotton trade — both driving large-scale storage demand." },
      { name: "Cold storage & warehousing", note: "Growing need for modern storage infrastructure to match commodity-market volumes." },
    ],
    faqs: [
      {
        q: "Can you build large storage sheds for agri-commodity trade in Guntur?",
        a: "Yes — commodity storage is one of the more common PEB shed use-cases we design for, sized to throughput and stacking requirements.",
      },
    ],
  },
  {
    slug: "kakinada",
    stateSlug: "andhra-pradesh",
    name: "Kakinada",
    distance: "~445 km from our Peerzadiguda facility",
    intro:
      "Kakinada is a key node on the Visakhapatnam–Chennai Industrial Corridor, with a significant port, SEZ and oil & gas processing presence. Vasavi Structures fabricates at our Hyderabad facility and delivers PEB structures to Kakinada project sites.",
    industrialZones: ["Kakinada Node, Visakhapatnam–Chennai Industrial Corridor", "Kakinada SEZ"],
    sectors: [
      { name: "Oil & gas processing", note: "Established hub for oil & gas-linked industry and ancillary units." },
      { name: "Port-linked logistics", note: "Kakinada's port drives cargo, warehousing and distribution infrastructure demand." },
    ],
    faqs: [
      {
        q: "Do you handle industrial structures for SEZ or port-linked projects in Kakinada?",
        a: "Yes — we design PEB structures for a range of industrial and logistics use cases; SEZ-specific compliance requirements are discussed during project scoping.",
      },
    ],
  },
  {
    slug: "tirupati",
    stateSlug: "andhra-pradesh",
    name: "Tirupati",
    distance: "~560 km from our Peerzadiguda facility",
    intro:
      "Tirupati sits near the Yerpedu–Srikalahasti industrial node on the Visakhapatnam–Chennai corridor and close to the Sri City SEZ, with a growing electronics and auto-ancillary manufacturing base. Vasavi Structures fabricates PEB structures at our Hyderabad facility for delivery to Tirupati-area sites.",
    industrialZones: ["Yerpedu–Srikalahasti Node, Visakhapatnam–Chennai Industrial Corridor"],
    sectors: [
      { name: "Electronics & auto-ancillary manufacturing", note: "Proximity to Sri City SEZ has driven a growing electronics and component-manufacturing base in the region." },
    ],
    faqs: [
      {
        q: "Can you build precision-manufacturing sheds near Sri City / Tirupati?",
        a: "Yes — electronics and auto-ancillary units typically need clean, well-ventilated PEB sheds with specific floor-flatness and utility routing; we scope these requirements during design.",
      },
    ],
  },
  {
    slug: "nellore",
    stateSlug: "andhra-pradesh",
    name: "Nellore",
    distance: "~410 km from our Peerzadiguda facility",
    intro:
      "Nellore is known for aquaculture exports and mica/mineral trade, with growing industrial activity along the coastal corridor. Vasavi Structures fabricates PEB and industrial shed structures at our Hyderabad facility for delivery to Nellore project sites.",
    industrialZones: ["Nellore district industrial estates"],
    sectors: [
      { name: "Aquaculture & seafood processing", note: "A major shrimp and seafood export hub, requiring processing and cold-storage-adjacent structures." },
      { name: "Mineral trade", note: "Established mica and mineral trading base in the region." },
    ],
    faqs: [
      {
        q: "Do you build sheds for aquaculture or seafood processing units in Nellore?",
        a: "Yes — processing and storage sheds for this sector have specific hygiene, drainage and ventilation needs that we design around.",
      },
    ],
  },
];

export const getLocationBySlug = (slug) => locations.find((l) => l.slug === slug);
export const getStateBySlug = (slug) => states.find((s) => s.slug === slug);
export const getCitiesByState = (stateSlug) => locations.filter((l) => l.stateSlug === stateSlug);
